import {
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  AlertTriangle,
  ArrowLeft,
  Clock,
  Globe,
  Mail,
  Monitor,
  ShieldAlert,
  User,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import adminApi from "../../api/admin";

const AdminAuditTrailDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["audit-trail", id],
    queryFn: async () => {
      const res = await adminApi.getAuditTrailDetails(id);
      return res.data;
    },
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <CircularProgress />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="text-center text-red-500 py-10">
        Failed to load audit entry.
      </div>
    );
  }

  const isFailed = data.action === "login_failed";

  return (
    <div className="max-w-6xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-10">
        <div>
          <Typography className="text-2xl font-semibold text-gray-900">
            Audit Trail Details
          </Typography>
          <Typography className="text-gray-500 text-sm mt-1">
            Detailed event information
          </Typography>
        </div>

        <Button
          startIcon={<ArrowLeft size={18} />}
          onClick={() => navigate(-1)}
          className="normal-case"
        >
          Back
        </Button>
      </div>

      {/* Event Summary */}
      <Card className="border border-gray-200 shadow-sm rounded-xl mb-6">
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <Typography className="text-sm text-gray-500">
                Event ID
              </Typography>

              <Typography className="text-xl font-semibold text-gray-900">
                #{data.id}
              </Typography>

              <div className="flex items-center gap-2 text-gray-500 text-sm mt-3">
                <Clock size={16} />
                {format(new Date(data.timestamp), "PPP p")}
              </div>
            </div>

            <Chip
              label={data.action_display}
              className={`font-medium ${
                isFailed
                  ? "bg-red-100 text-red-700"
                  : "bg-blue-100 text-blue-700"
              }`}
            />
          </div>
        </CardContent>
      </Card>

      {/* User Info */}
      <Card className="border border-gray-200 shadow-sm rounded-xl mb-6">
        <CardContent className="p-6">
          <Typography className="font-semibold text-gray-800 mb-6">
            User Information
          </Typography>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            <InfoItem
              icon={<Mail size={16} />}
              label="Login Email"
              value={data.login_email}
            />

            <InfoItem
              icon={<User size={16} />}
              label="User Email"
              value={data.user_email || "N/A"}
            />

            <InfoItem
              icon={<ShieldAlert size={16} />}
              label="IP Address"
              value={data.ip_address}
            />

            <InfoItem
              icon={<Globe size={16} />}
              label="MAC Address"
              value={data.mac_address || "N/A"}
            />
          </div>
        </CardContent>
      </Card>

      {/* Device Info */}
      <Card className="border border-gray-200 shadow-sm rounded-xl mb-6">
        <CardContent className="p-6">
          <Typography className="font-semibold text-gray-800 mb-6">
            Device & Environment
          </Typography>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            <InfoItem
              icon={<Monitor size={16} />}
              label="Device Type"
              value={data.device_type_display}
            />

            <InfoItem
              icon={<Monitor size={16} />}
              label="Browser"
              value={data.browser_name}
            />

            <InfoItem
              icon={<Monitor size={16} />}
              label="Operating System"
              value={data.browser_os}
            />

            <InfoItem
              icon={<ShieldAlert size={16} />}
              label="Session Key"
              value={data.session_key || "N/A"}
            />
          </div>
        </CardContent>
      </Card>

      {/* Error Section */}
      {data.extra_data?.errors && (
        <Card className="border border-red-200 bg-red-50 rounded-xl mb-6">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle size={18} className="text-red-600" />
              <Typography className="font-semibold text-red-700">
                Error Details
              </Typography>
            </div>

            <Divider className="mb-4" />

            <pre className="text-sm text-red-700 whitespace-pre-wrap">
              {JSON.stringify(data.extra_data.errors, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}

      {/* User Agent */}
      <Card className="border border-gray-200 shadow-sm rounded-xl">
        <CardContent className="p-6">
          <Typography className="font-semibold text-gray-800 mb-3">
            User Agent
          </Typography>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <pre className="text-xs text-gray-600 whitespace-pre-wrap break-words">
              {data.user_agent}
            </pre>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const InfoItem = ({ icon, label, value }) => (
  <div className="flex items-start gap-3">
    <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100 text-gray-600">
      {icon}
    </div>

    <div>
      <Typography className="text-xs text-gray-500 uppercase tracking-wide">
        {label}
      </Typography>

      <Typography className="text-sm font-medium text-gray-900 mt-1">
        {value}
      </Typography>
    </div>
  </div>
);

export default AdminAuditTrailDetailsPage;